import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import { AgGridReact } from "ag-grid-react";
import {
  attendancelist,
  attendancestafflist,
} from "../../../services/attendance";
import exportUrl from "../../../exportUrl";

const today = moment().format("YYYY-MM-DD");
const isAdmin = localStorage.getItem("Role") === "1";

const TYPE_SHORT = { present: "P", absent: "A", leave: "L", on_duty: "O", lop: "LOP" };
const TYPE_COLOR = { present: "#28a745", absent: "#dc3545", leave: "#ffc107", on_duty: "#17a2b8", lop: "#e67e22" };
const TYPE_LABEL = { present: "Present", absent: "Absent", leave: "Leave", on_duty: "On Duty", lop: "LOP" };

export const AttendanceReport = () => {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [date, setDate] = useState(today);
  const [userId, setUserId] = useState("");
  const [attendanceType, setAttendanceType] = useState("");
  const [viewMode, setViewMode] = useState("daily");
  const [loading, setLoading] = useState(false);

  // ─── Monthly matrix state ──────────────────────────────────────────────
  const [monthYear, setMonthYear] = useState(moment().format("YYYY-MM"));
  const [matrixRows, setMatrixRows] = useState([]);
  const [daysInMonth, setDaysInMonth] = useState(0);

  // ─── Column defs for daily view ────────────────────────────────────────
  const columnDefs = [
    {
      headerName: "S.No",
      valueGetter: "node.rowIndex + 1",
      width: 80,
      sortable: false,
      filter: false,
    },
    ...(isAdmin
      ? [
        {
          headerName: "Staff",
          field: "user.name",
          sortable: true,
          resizable: true,
          filter: true,
          floatingFilter: true,
        },
      ]
      : []),
    {
      headerName: "Date",
      field: "date",
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: true,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format("DD/MM/YYYY") : "-",
    },
    {
      headerName: "Attendance",
      field: "attendance_type",
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: true,
      width: 140,
      cellRenderer: (params) => {
        const type = params.value || "present";
        return (
          <span
            className="badge"
            style={{
              backgroundColor: TYPE_COLOR[type] || "#6c757d",
              fontSize: "13px",
              padding: "5px 12px",
            }}
          >
            {TYPE_LABEL[type] || type}
          </span>
        );
      },
    },
    {
      headerName: "Clock In",
      field: "clock_in",
      sortable: true,
      resizable: true,
      filter: true,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format("hh:mm A") : "-",
      cellRenderer: (params) => (
        <span>
          {params.value ? moment(params.value).format("hh:mm A") : "-"}
          {params.data.is_late ? (
            <span className="badge bg-warning ms-1" style={{ fontSize: "10px" }}>Late</span>
          ) : null}
        </span>
      ),
    },
    {
      headerName: "Clock Out",
      field: "clock_out",
      sortable: true,
      resizable: true,
      filter: true,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format("hh:mm A") : "-",
    },
    {
      headerName: "Hours",
      field: "working_hours",
      sortable: true,
      resizable: true,
      filter: true,
      width: 100,
      valueFormatter: (params) => (params.value != null ? params.value + "h" : "-"),
    },
  ];

  // ─── Monthly matrix column defs ────────────────────────────────────────
  const matrixColumnDefs = useMemo(() => {
    if (!daysInMonth) return [];
    const cols = [
      {
        headerName: "S.No",
        valueGetter: "node.rowIndex + 1",
        width: 70,
        sortable: false,
        filter: false,
        pinned: "left",
      },
      {
        headerName: "Staff",
        field: "staffName",
        width: 160,
        sortable: true,
        filter: true,
        pinned: "left",
      },
    ];
    for (let d = 1; d <= daysInMonth; d++) {
      const dayStr = String(d).padStart(2, "0");
      cols.push({
        headerName: String(d),
        field: `d${dayStr}`,
        width: 55,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center", padding: "2px" },
        cellRenderer: (params) => {
          const val = params.value;
          if (!val) return <span style={{ color: "#ccc" }}>-</span>;
          return (
            <span
              style={{
                display: "inline-block",
                width: "30px",
                height: "30px",
                lineHeight: "30px",
                borderRadius: "50%",
                backgroundColor: TYPE_COLOR[val] || "#eee",
                color: "#fff",
                fontWeight: 700,
                fontSize: "13px",
              }}
            >
              {TYPE_SHORT[val] || "?"}
            </span>
          );
        },
      });
    }
    // Totals columns
    cols.push(
      { headerName: "P", field: "_totalP", width: 50, cellStyle: { textAlign: "center", fontWeight: 700, color: "#28a745" } },
      { headerName: "A", field: "_totalA", width: 50, cellStyle: { textAlign: "center", fontWeight: 700, color: "#dc3545" } },
      { headerName: "L", field: "_totalL", width: 50, cellStyle: { textAlign: "center", fontWeight: 700, color: "#ffc107" } },
      { headerName: "O", field: "_totalO", width: 50, cellStyle: { textAlign: "center", fontWeight: 700, color: "#17a2b8" } },
      { headerName: "LOP", field: "_totalLOP", width: 55, cellStyle: { textAlign: "center", fontWeight: 700, color: "#e67e22" } },
    );
    return cols;
  }, [daysInMonth]);

  // ─── Load staff list + initial data on mount ──────────────────────────
  useEffect(() => {
    const userBranch = localStorage.getItem("branch");
    if (isAdmin) {
      attendancestafflist({ branch: userBranch }).then((res) => {
        if (res && res.status === true) setStaff(res.data.staff);
      });
    }
    loadDaily({ date: today, branch: userBranch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Load daily ───────────────────────────────────────────────────────
  const loadDaily = async (params = {}) => {
    setLoading(true);
    if (!params.date) params.date = date;
    if (!params.branch) params.branch = localStorage.getItem("branch");
    const res = await attendancelist(params);
    if (res && res.status === true) {
      setRows(res.data.attendances.data);
    }
    setLoading(false);
  };

  // ─── Load monthly matrix ──────────────────────────────────────────────
  const loadMonthly = async (targetMonth, filterUserId) => {
    setLoading(true);
    const m = moment(targetMonth || monthYear, "YYYY-MM");
    const fromDate = m.startOf("month").format("YYYY-MM-DD");
    const toDate = m.endOf("month").format("YYYY-MM-DD");
    const numDays = m.daysInMonth();
    setDaysInMonth(numDays);

    const userBranch = localStorage.getItem("branch");
    // Use passed filterUserId if provided, otherwise state
    const activeUserId = filterUserId !== undefined ? filterUserId : userId;

    // Get relevant staff list
    let staffList = [];
    if (isAdmin) {
      // Always fetch fresh staff list for branch to avoid async timing issues
      const staffRes = await attendancestafflist({ branch: userBranch });
      const allStaff = (staffRes?.status && staffRes.data.staff) || [];
      if (allStaff.length > 0) setStaff(allStaff);

      if (activeUserId) {
        const s = allStaff.find((s) => String(s.id) === String(activeUserId));
        if (s) staffList = [s];
      } else {
        staffList = allStaff;
      }
    } else {
      staffList = [{ id: localStorage.getItem("id") || "me", name: "My Attendance" }];
    }

    if (staffList.length === 0) {
      setMatrixRows([]);
      setDaysInMonth(0);
      setLoading(false);
      return;
    }

    // Fetch all attendance for the month (paginate max)
    const res = await attendancelist({
      from_date: fromDate,
      to_date: toDate,
      user_id: activeUserId,
      branch: userBranch,
      per_page: 1000,
    });

    // Build lookup: userId_date -> attendance_type
    const lookup = {};
    if (res && res.status === true) {
      (res.data.attendances.data || []).forEach((att) => {
        const normalizedDate = moment(att.date).format("YYYY-MM-DD");
        const key = `${att.user_id}_${normalizedDate}`;
        lookup[key] = att.attendance_type || "present";
      });
    }

    // Build matrix rows — use m for date construction to avoid stale state
    const monthStr = m.format("YYYY-MM");
    const matrix = staffList.map((s) => {
      const row = {
        staffName: s.name,
        _totalP: 0,
        _totalA: 0,
        _totalL: 0,
        _totalO: 0,
        _totalLOP: 0,
      };
      for (let d = 1; d <= numDays; d++) {
        const dayStr = String(d).padStart(2, "0");
        const dateStr = `${monthStr}-${dayStr}`;
        const key = `${s.id}_${dateStr}`;
        const val = lookup[key] || null;
        row[`d${dayStr}`] = val;
        if (val === "present") row._totalP++;
        else if (val === "absent") row._totalA++;
        else if (val === "leave") row._totalL++;
        else if (val === "on_duty") row._totalO++;
        else if (val === "lop") row._totalLOP++;
      }
      return row;
    });

    setMatrixRows(matrix);
    setLoading(false);
  };

  // ─── Handle filter submit ─────────────────────────────────────────────
  function handlefilter(e) {
    e.preventDefault();
    const userBranch = localStorage.getItem("branch");
    if (viewMode === "monthly") {
      loadMonthly();
    } else {
      loadDaily({
        date,
        user_id: userId,
        attendance_type: attendanceType,
        branch: userBranch,
      });
    }
  }

  function handlereset() {
    const userBranch = localStorage.getItem("branch");
    setDate(today);
    setMonthYear(moment().format("YYYY-MM"));
    setUserId("");
    setAttendanceType("");
    if (viewMode === "monthly") loadMonthly(moment().format("YYYY-MM"), "");
    else loadDaily({ date: today, branch: userBranch });
  }

  function toggleView(mode) {
    const userBranch = localStorage.getItem("branch");
    setViewMode(mode);
    setUserId("");
    setAttendanceType("");
    if (mode === "monthly") {
      const thisMonth = moment().format("YYYY-MM");
      setMonthYear(thisMonth);
      loadMonthly(thisMonth, "");
    } else {
      setDate(today);
      loadDaily({ date: today, branch: userBranch });
    }
  }

  

  const summaryStats = useMemo(() => {
    if (viewMode === "daily") {
      const stats = { present: 0, absent: 0, leave: 0, on_duty: 0, lop: 0, total: rows.length };
      rows.forEach((r) => {
        const t = r.attendance_type || "present";
        if (stats[t] !== undefined) stats[t]++;
      });
      return stats;
    } else {
      const stats = { present: 0, absent: 0, leave: 0, on_duty: 0, lop: 0, total: 0 };
      matrixRows.forEach((r) => {
        stats.present += r._totalP || 0;
        stats.absent += r._totalA || 0;
        stats.leave += r._totalL || 0;
        stats.on_duty += r._totalO || 0;
        stats.lop += r._totalLOP || 0;
      });
      stats.total = stats.present + stats.absent + stats.leave + stats.on_duty + stats.lop;
      return stats;
    }
  }, [viewMode, rows, matrixRows]);

  function downloadfile(dtype) {
    let params;

    if (viewMode === "monthly") {
      const fromDate = moment(monthYear, "YYYY-MM").startOf("month").format("YYYY-MM-DD");
      const toDate = moment(monthYear, "YYYY-MM").endOf("month").format("YYYY-MM-DD");

      params =
        "from_date=" + fromDate +
        "&to_date=" + toDate +
        "&user_id=" + (userId || "") +
        "&branch=" + (localStorage.getItem("branch") || "") +
        "&report=monthly" +
        "&type=" + dtype;
      window.open(exportUrl + "report/monthlyattendancedownload?" + params, "_blank");

    } else {
      params =
        "date=" + (date || "") +
        "&user_id=" + (userId || "") +
        "&attendance_type=" + (attendanceType || "") +
        "&branch=" + (localStorage.getItem("branch") || "") +
        "&type=" + dtype;
      window.open(exportUrl + "report/attendancedownload?" + params, "_blank");

    }

  }

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="app-content main-content">
      <div className="side-app main-container">
        <div className="page-header d-xl-flex d-block">
          <div className="page-leftheader">
            <div className="page-title">Attendance Report</div>
          </div>
        </div>

        {/* Filters */}
        <div className="row">
          <div className="col-xl-12">
            <div className="card">
              <div className="card-header d-flex align-items-center flex-wrap gap-2">
                <h4 className="card-title">Filters</h4>
                <div className="ms-auto d-flex gap-2 flex-wrap">
                  <div className="btn-group btn-group-sm">
                    <button
                      className={`btn ${viewMode === "daily" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => toggleView("daily")}
                    >
                      Daily
                    </button>
                    <button
                      className={`btn ${viewMode === "monthly" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => toggleView("monthly")}
                    >
                      Monthly
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <form onSubmit={handlefilter}>
                  <div className="row">
                    <div className="col-md-3">
                      <div className="form-group">
                        <label className="form-label">
                          {viewMode === "monthly" ? "Month" : "Date"}
                        </label>
                        <input
                          type={viewMode === "monthly" ? "month" : "date"}
                          className="form-control"
                          value={viewMode === "monthly" ? monthYear : date}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (viewMode === "monthly") {
                              setMonthYear(val);
                              loadMonthly(val, userId);
                            } else {
                              setDate(val);
                              loadDaily({ date: val, user_id: userId, attendance_type: attendanceType });
                            }
                          }}
                        />
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="col-md-3">
                        <div className="form-group">
                          <label className="form-label">Staff</label>
                          <select
                            className="form-control"
                            value={userId}
                            // onChange={(e) => setUserId(e.target.value)}
                            onChange={(e) => {
                              const selectedUserId = e.target.value;
                              setUserId(selectedUserId);
                              if (viewMode === "monthly") {
                                loadMonthly(monthYear, selectedUserId);
                              } else {
                                loadDaily({
                                  date,
                                  user_id: selectedUserId,
                                  attendance_type: attendanceType,
                                });
                              }
                            }}
                          >
                            <option value="">All Staff</option>
                            {staff.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    {viewMode === "daily" && (
                      <div className="col-md-3">
                        <div className="form-group">
                          <label className="form-label">Type</label>
                          <select
                            className="form-control"
                            value={attendanceType}
                            onChange={(e) => {
                              const type = e.target.value;
                              setAttendanceType(type);
                              loadDaily({ date, user_id: userId, attendance_type: type });
                            }}
                          // onChange={(e) => setAttendanceType(e.target.value)}
                          >
                            <option value="">All</option>
                            <option value="present">Present</option>
                            <option value="absent">Absent</option>
                            <option value="leave">Leave</option>
                            <option value="on_duty">On Duty</option>
                            <option value="lop">LOP</option>
                          </select>
                        </div>
                      </div>
                    )}
                    <div className="col-md-3 d-flex align-items-end">
                      <div className="form-group w-100">
                        <div className="d-flex gap-2">
                          <button type="submit" className="btn btn-primary me-2" disabled={loading}>
                            <i className="fa fa-search me-1"></i> {loading ? "Loading..." : "Search"}
                          </button>
                          <button type="button" className="btn btn-secondary" onClick={handlereset}>
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {(viewMode === "daily" || viewMode === "monthly") && (
                    <div className="text-end mt-2">
                      <button type="button" className="btn btn-danger me-2 btn-sm" onClick={() => downloadfile("pdf")}>
                        <i className="fa fa-file-pdf me-1"></i> PDF
                      </button>
                      <button type="button" className="btn btn-success btn-sm" onClick={() => downloadfile("excel")}>
                        <i className="fa fa-file-excel me-1"></i> Excel
                      </button>
                    </div>
                  )}
                </form>

                {viewMode === "monthly" && (
                  <div className="mt-2 text-muted small">
                    <span className="me-3"><span style={{ display: "inline-block", width: "14px", height: "14px", borderRadius: "50%", backgroundColor: "#28a745", marginRight: "4px" }}></span> P = Present</span>
                    <span className="me-3"><span style={{ display: "inline-block", width: "14px", height: "14px", borderRadius: "50%", backgroundColor: "#dc3545", marginRight: "4px" }}></span> A = Absent</span>
                    <span className="me-3"><span style={{ display: "inline-block", width: "14px", height: "14px", borderRadius: "50%", backgroundColor: "#ffc107", marginRight: "4px" }}></span> L = Leave</span>
                    <span className="me-3"><span style={{ display: "inline-block", width: "14px", height: "14px", borderRadius: "50%", backgroundColor: "#17a2b8", marginRight: "4px" }}></span> O = On Duty</span>
                    <span><span style={{ display: "inline-block", width: "14px", height: "14px", borderRadius: "50%", backgroundColor: "#e67e22", marginRight: "4px" }}></span> LOP = LOP</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="row">
          <div className="col-xl-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">
                  {viewMode === "monthly" ? `Monthly Attendance — ${moment(monthYear, "YYYY-MM").format("MMMM YYYY")}` : "Daily Attendance Records"}
                </h4>
                <span className="ms-auto text-muted">
                  {viewMode === "monthly" ? `${matrixRows.length} staff` : `${rows.length} record(s)`}
                </span>
              </div>
              <div className="card-body" style={{ overflowX: "auto" }}>
                {viewMode === "monthly" ? (
                  <div className="ag-theme-alpine" style={{ minWidth: `${matrixColumnDefs.length * 55 + 230}px` }}>
                    <AgGridReact
                      columnDefs={matrixColumnDefs}
                      rowData={matrixRows}
                      pagination="true"
                      paginationPageSize="20"
                      suppressDragLeaveHidesColumns={true}
                      domLayout="autoHeight"
                    />
                  </div>
                ) : (
                  <div className="ag-theme-alpine ag-style mt-2">
                    <AgGridReact
                      columnDefs={columnDefs}
                      rowData={rows}
                      pagination="true"
                      paginationPageSize="15"
                      sortingOrder={["asc", "desc"]}
                      suppressDragLeaveHidesColumns={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceReport;
